/**
 * ASCII Art Generator - 主渲染器
 *
 * 统一管理所有渲染引擎，根据设置选择正确的引擎进行渲染
 * Wave 8.3: 集成灰度预计算缓存
 */

import type {
  IRenderEngine,
  FullRenderSettings,
  RenderResult,
  StyleType,
} from './types'
import type { AnimationContext } from './animation/types'
import type { GrayscaleCacheData } from './algorithms/grayscale'
import {
  ClassicEngine,
  BrailleEngine,
  HalftoneEngine,
  DitherEngine,
  Dither2Engine,
  DotCrossEngine,
  LineEngine,
  ParticlesEngine,
  ClaudeEngine,
  RetroEngine,
  TerminalEngine,
} from './engines'
import { DEFAULT_SETTINGS } from './defaults'
import {
  globalGrayscaleCache,
} from './algorithms/grayscale'
import { isGrayscaleCacheEnabled } from './performance/features'

/**
 * 渲染上下文（扩展动画上下文，包含灰度缓存）
 */
export interface RenderContext extends AnimationContext {
  /** 灰度预计算缓存（可选） */
  grayscaleCache?: GrayscaleCacheData | null
}

/**
 * 主渲染器类
 * 负责管理渲染引擎、处理渲染请求
 * Wave 8.3: 管理灰度预计算缓存
 */
export class Renderer {
  private engines: Map<StyleType, IRenderEngine>
  private currentSettings: FullRenderSettings
  private grayscaleCache: typeof globalGrayscaleCache

  constructor() {
    this.engines = new Map()
    this.currentSettings = { ...DEFAULT_SETTINGS }
    this.grayscaleCache = globalGrayscaleCache

    // 注册所有渲染引擎
    this.registerEngine(new ClassicEngine())
    this.registerEngine(new BrailleEngine())
    this.registerEngine(new HalftoneEngine())
    this.registerEngine(new DitherEngine())
    this.registerEngine(new Dither2Engine())
    this.registerEngine(new DotCrossEngine())
    this.registerEngine(new LineEngine())
    this.registerEngine(new ParticlesEngine())
    this.registerEngine(new ClaudeEngine())
    this.registerEngine(new RetroEngine())
    this.registerEngine(new TerminalEngine())
  }

  /**
   * 注册渲染引擎
   */
  registerEngine(engine: IRenderEngine): void {
    this.engines.set(engine.name, engine)
  }

  /**
   * 获取当前设置
   */
  getSettings(): FullRenderSettings {
    return { ...this.currentSettings }
  }

  /**
   * 更新设置
   */
  updateSettings(settings: Partial<FullRenderSettings>): void {
    this.currentSettings = {
      ...this.currentSettings,
      ...settings,
    }
  }

  /**
   * 重置为默认设置
   */
  resetSettings(): void {
    this.currentSettings = { ...DEFAULT_SETTINGS }
  }

  /**
   * 渲染图像（支持动画）
   *
   * @param source 源图像
   * @param canvas 目标 canvas
   * @param settings 可选的渲染设置覆盖
   * @param context 可选的动画上下文
   */
  render(
    source: HTMLImageElement | HTMLCanvasElement | ImageData,
    canvas: HTMLCanvasElement,
    settings?: Partial<FullRenderSettings>,
    context?: AnimationContext
  ): RenderResult {
    // 合并设置
    const finalSettings = settings
      ? { ...this.currentSettings, ...settings }
      : this.currentSettings

    // Wave 8.3: 获取或创建灰度缓存（如果启用）
    let grayscaleCache: GrayscaleCacheData | null = null
    if (isGrayscaleCacheEnabled() && source) {
      grayscaleCache = this.grayscaleCache.createOrUpdate(
        source,
        finalSettings.contrast,
        finalSettings.brightness
      )
    }

    // 获取对应的渲染引擎
    const engine = this.engines.get(finalSettings.style)

    if (!engine) {
      // 如果引擎未实现，回退到Classic引擎
      console.warn(
        `Engine "${finalSettings.style}" not implemented, falling back to Classic`
      )
      const fallbackEngine = this.engines.get('classic')!
      return fallbackEngine.render(source, finalSettings, canvas)
    }

    // 构建渲染上下文（包含灰度缓存）
    const renderContext: RenderContext = context ? {
      ...context,
      grayscaleCache
    } : {
      time: 0,
      deltaTime: 0,
      frameCount: 0,
      mouseState: { x: 0, y: 0, isInCanvas: false },
      grayscaleCache
    }

    // 检查引擎是否支持动画渲染
    // 使用类型断言调用带动画上下文的渲染方法
    if (context && 'render' in engine) {
      // DitherEngine 和 Dither2Engine 支持 context 参数
      // 其他引擎会忽略 context
      return (engine as any).render(source, finalSettings, canvas, renderContext)
    }

    return engine.render(source, finalSettings, canvas)
  }

  /**
   * 渲染动画帧（便捷方法）
   *
   * @param source 源图像
   * @param canvas 目标 canvas
   * @param context 动画上下文
   */
  renderAnimated(
    source: HTMLImageElement | HTMLCanvasElement | ImageData,
    canvas: HTMLCanvasElement,
    context: AnimationContext
  ): RenderResult {
    return this.render(source, canvas, undefined, context)
  }

  /**
   * 清除灰度缓存
   * Wave 8.3: 当源图像变化时调用
   */
  clearGrayscaleCache(): void {
    this.grayscaleCache.clear()
  }

  /**
   * 更新灰度缓存（用于外部触发）
   * Wave 8.3: 图片加载时调用
   */
  updateGrayscaleCache(
    source: HTMLImageElement | HTMLCanvasElement | ImageData,
    contrast: number,
    brightness: number
  ): GrayscaleCacheData | null {
    if (!isGrayscaleCacheEnabled()) {
      return null
    }
    return this.grayscaleCache.createOrUpdate(source, contrast, brightness)
  }

  /**
   * 获取灰度缓存（供外部访问）
   */
  getGrayscaleCache(): GrayscaleCacheData | null {
    return this.grayscaleCache.get()
  }

  /**
   * 获取输出尺寸
   */
  getOutputSize(
    imageWidth: number,
    imageHeight: number,
    style?: StyleType
  ): { width: number; height: number } {
    const finalStyle = style || this.currentSettings.style
    const engine = this.engines.get(finalStyle) || this.engines.get('classic')!
    return engine.getOutputSize(imageWidth, imageHeight, this.currentSettings)
  }

  /**
   * 检查引擎是否已注册
   */
  hasEngine(style: StyleType): boolean {
    return this.engines.has(style)
  }

  /**
   * 获取所有已注册的引擎
   */
  getRegisteredEngines(): StyleType[] {
    return Array.from(this.engines.keys())
  }

  /**
   * 检查当前风格是否支持动画
   */
  supportsAnimation(_style?: StyleType): boolean {
    // 所有引擎都支持动画
    return true
  }
}

// 导出单例实例
export const renderer = new Renderer()

// 导出创建渲染器的工厂函数
export function createRenderer(): Renderer {
  return new Renderer()
}